import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Games as GamesIcon,
  Quiz as QuizIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SavedWordsMatch from './SavedWordsMatch'
import SavedWordsQuiz from './SavedWordsQuiz'

const SavedWordsGames = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState('A1')
  const [wordCount, setWordCount] = useState(0)
  const [cardsEnabled, setCardsEnabled] = useState(false)
  const [currentPage, setCurrentPage] = useState('saved-words-games')

  useEffect(() => {
    if (user && userData) {
      const level = userData.level || 'A1'
      setCurrentLevel(level)
      checkWordCountForLevel(level)
    }
  }, [user, userData])

  const checkWordCountForLevel = async (level) => {
    if (!user) {
      toast.error('Giriş yapmanız gerekiyor')
      return
    }

    try {
      console.log('=== CHECKING WORD COUNT FOR LEVEL:', level, '===')

      // Kullanıcının kaydedilen kelimelerini kontrol et
      const savedWordsRef = collection(db, 'users', user.uid, 'savedWords', level, 'words')
      const savedWordsSnapshot = await getDocs(savedWordsRef)
      const count = savedWordsSnapshot.size

      console.log('Word count in', level + ':', count)
      setWordCount(count)

      if (count >= 10) {
        console.log('ENOUGH WORDS - Enabling cards')
        setCardsEnabled(true)
      } else {
        console.log('NOT ENOUGH WORDS - Disabling cards')
        setCardsEnabled(false)
      }

      setLoading(false)
    } catch (error) {
      console.error('Error checking word count:', error)
      toast.error('Kelime sayısı kontrol edilemedi')
      setLoading(false)
    }
  }

  const handleGameClick = (gameType) => {
    if (!cardsEnabled) {
      toast.error('Bu oyunları oynayabilmek için en az 10 kelime çalışılmış olmalı')
      return
    }

    console.log('Starting game:', gameType, 'for level:', currentLevel)
    
    // Saved words oyunlarına yönlendir
    if (gameType === 'match') {
      setCurrentPage('saved-words-match')
    } else if (gameType === 'quiz') {
      setCurrentPage('saved-words-quiz')
    }
  }

  const handleBackToSavedWordsGames = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('saved-words-games')
    }
  }

  const gameItems = [
    {
      id: 'match',
      title: 'Kelime Eşleştirme',
      description: cardsEnabled 
        ? `${currentLevel} seviyesinde çalışılanlar listesinde ${wordCount} kelime bulunuyor.\nBu oyunları oynayabilmek için yeterli çalışılan kelime sayısına sahipsin.`
        : `${currentLevel} seviyesinde çalışılanlar listesinde sadece ${wordCount} kelime bulunuyor.\nBu oyunları oynayabilmek için listende en az 10 kelime çalışılıyor olmalı.`,
      icon: <GamesIcon sx={{ fontSize: 48, color: cardsEnabled ? 'primary.main' : 'grey.400' }} />,
      color: cardsEnabled ? 'primary' : 'grey',
      action: () => handleGameClick('match'),
      enabled: cardsEnabled
    },
    {
      id: 'quiz',
      title: 'Anlam Sorma',
      description: cardsEnabled 
        ? `${currentLevel} seviyesinde çalışılanlar listesinde ${wordCount} kelime bulunuyor.\nBu oyunları oynayabilmek için yeterli çalışılan kelime sayısına sahipsin.`
        : `${currentLevel} seviyesinde çalışılanlar listesinde sadece ${wordCount} kelime bulunuyor.\nBu oyunları oynayabilmek için listende en az 10 kelime çalışılıyor olmalı.`,
      icon: <QuizIcon sx={{ fontSize: 48, color: cardsEnabled ? 'success.main' : 'grey.400' }} />,
      color: cardsEnabled ? 'success' : 'grey',
      action: () => handleGameClick('quiz'),
      enabled: cardsEnabled
    }
  ]

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                console.log('SavedWordsGames back button clicked, onBack:', onBack)
                if (onBack && typeof onBack === 'function') {
                  onBack()
                } else {
                  console.error('onBack is not a function:', onBack)
                }
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Kelime Oyunlarım
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Saved Words Match oyununu göster
  if (currentPage === 'saved-words-match') {
    return <SavedWordsMatch 
      userData={userData} 
      onBack={handleBackToSavedWordsGames}
      userLevel={currentLevel}
    />
  }

  // Saved Words Quiz oyununu göster
  if (currentPage === 'saved-words-quiz') {
    return <SavedWordsQuiz 
      userData={userData} 
      onBack={handleBackToSavedWordsGames}
      userLevel={currentLevel}
    />
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              console.log('SavedWordsGames main back button clicked, onBack:', onBack)
              if (onBack && typeof onBack === 'function') {
                onBack()
              } else {
                console.error('onBack is not a function:', onBack)
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kelime Oyunlarım
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: 4,
          backgroundColor: '#ffffff',
          border: '2px solid #1976d2'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <BookmarkIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Kelime Oyunlarım
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviye: {currentLevel}
            </Typography>
          </CardContent>
        </Card>

        {/* Games Grid */}
        <Grid container spacing={3} sx={{ mt: 2, px: 2 }}>
          {gameItems.map((game) => (
            <Grid item xs={12} key={game.id}>
              <Card 
                sx={{ 
                  borderRadius: 3,
                  boxShadow: 4,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '2px solid #e0e0e0',
                  cursor: game.enabled ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  opacity: game.enabled ? 1 : 0.6,
                  '&:hover': game.enabled ? {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                    borderColor: '#1976d2'
                  } : {}
                }}
                onClick={game.enabled ? game.action : undefined}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2 }}>
                      {game.icon}
                    </Box>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold',
                      color: game.enabled ? '#1976d2' : 'grey.500'
                    }}>
                      {game.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    opacity: 0.8,
                    whiteSpace: 'pre-line'
                  }}>
                    {game.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Info Alert */}
        <Alert severity="info" sx={{ 
          mt: 4, 
          mx: 2, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          border: '2px solid #1976d2'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            🎮 Bu oyunlar sadece çalışılan kelimelerinizle oynanır. 
            En az 10 kelime çalıştığınızda oyunlar aktif olacaktır.
          </Typography>
        </Alert>
      </Container>
    </Box>
  )
}

export default SavedWordsGames
